import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AudioPlayer from '../../../components/AudioPlayer';
import { followVoiceActor, unfollowVoiceActor, isFollowing } from '../../../services/voiceActorService';
import { useAuth } from '../../../contexts/AuthContext';

const LineInteractionModal = ({ line, onClose, onWordSelect, glossaryWords }) => {
  const [notes, setNotes] = useState('');
  const [isFollowingActor, setIsFollowingActor] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (line?.voiceActor?.id && user) {
      checkFollowStatus();
    }
  }, [line?.voiceActor?.id, user]);

  const checkFollowStatus = async () => {
    try {
      const following = await isFollowing(line?.voiceActor?.id);
      setIsFollowingActor(following);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const handleFollowToggle = async () => {
    if (!line?.voiceActor?.id) return;
    
    try {
      setFollowLoading(true);
      if (isFollowingActor) {
        await unfollowVoiceActor(line?.voiceActor?.id);
        setIsFollowingActor(false);
      } else {
        await followVoiceActor(line?.voiceActor?.id);
        setIsFollowingActor(true);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  const findGlossaryWords = () => {
    if (!line?.dialogue || !glossaryWords) return [];
    
    const words = glossaryWords?.filter(word => 
      line?.dialogue?.toLowerCase()?.includes(word?.word?.toLowerCase())
    );
    
    return words;
  };

  const relevantWords = findGlossaryWords();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-primary mb-1">{line?.character}</h3>
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                {line?.gid && (
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">
                    #{String(line?.gid)?.padStart(4, '0')}
                  </span>
                )}
                {line?.act && line?.scene && (
                  <span>
                    Act {line?.act}, Scene {line?.scene}
                    {line?.lineInScene ? ` · Line ${line?.lineInScene}` : ''}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Quote" size={16} className="text-accent" />
              Dialogue
            </h4>
            <p className="text-foreground text-lg leading-relaxed">{line?.dialogue}</p>
          </div>

          {line?.audioUrl && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Volume2" size={16} className="text-secondary" />
                Audio Pronunciation
              </h4>
              <AudioPlayer
                audioUrl={line?.audioUrl}
                label="Play Line Audio"
                size="md"
                variant="default"
                showDuration={true}
                onPlayStart={() => {}}
                onPlayEnd={() => {}}
              />
              
              {line?.voiceActor && (
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {line?.voiceActor?.headshotUrl ? (
                        <img
                          src={line?.voiceActor?.headshotUrl}
                          alt={line?.voiceActor?.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                          <Icon name="User" size={24} className="text-accent" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-muted-foreground">Performed by</p>
                        <p className="font-semibold text-foreground">{line?.voiceActor?.name}</p>
                      </div>
                    </div>
                    {user && (
                      <Button
                        variant={isFollowingActor ? "outline" : "default"}
                        size="sm"
                        onClick={handleFollowToggle}
                        disabled={followLoading}
                        iconName={isFollowingActor ? "UserCheck" : "UserPlus"}
                        iconPosition="left"
                      >
                        {followLoading ? 'Loading...' : isFollowingActor ? 'Following' : 'Follow'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {relevantWords?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="BookOpen" size={16} className="text-accent" />
                Related Glossary Terms
              </h4>
              <div className="space-y-2">
                {relevantWords?.map((word) => (
                  <button
                    key={word?.id}
                    onClick={() => onWordSelect(word)}
                    className="w-full text-left p-3 bg-muted/50 hover:bg-muted rounded-lg transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-primary group-hover:text-accent transition-colors">
                        {word?.word}
                      </span>
                      <Icon name="ExternalLink" size={14} className="text-muted-foreground group-hover:text-accent transition-colors" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{word?.definition}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="StickyNote" size={16} className="text-accent" />
              Personal Notes
            </h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              placeholder="Add your thoughts about this line..."
              className="w-full min-h-[100px] p-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent resize-none"
            />
          </div>
        </div>

        <div className="p-6 border-t border-border flex items-center justify-end gap-3">
          <Button
            variant="outline"
            size="sm"
            iconName="Bookmark"
            iconPosition="left"
          >
            Bookmark Line
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LineInteractionModal;